/* eslint-disable @typescript-eslint/no-explicit-any */
import { ButtonProps, ChipProps, TextFieldProps } from '@mui/material';
import type { Components, PaletteColor, Theme } from '@mui/material/styles';
import type { TColorRamp, TGlassSurface } from 'themes/tokens';
import { buildGradient } from 'themes/tokens';
import { alpha } from '@mui/material/styles';

/**
 * Shared button geometry + type, straight from Figma (Design page, Buttons
 * Container). Primary, secondary and tertiary are identical here — only the
 * fill and label colour differ — so it lives in one place.
 *
 * 52px tall · 8/24 padding · pill radius · actionLarge (Inter 600 18/24).
 */
/**
 * Glass button edge, translated from Figma's "Secondary Button" BG layer.
 *
 * Figma gives that button NO stroke. The edge is drawn entirely with drop
 * shadows at zero blur, offset only on X (-1.25 / +1.25) in #D0D0D0, so the
 * rim reads down the left and right and stays open across the top and bottom
 * — a lens edge, where light passes through the sides. It pairs that with a
 * soft ambient lift and inner shadows that put the light at the BOTTOM
 * (rgba(230,230,230) from below) and a faint shade at the top.
 *
 * The obvious web shortcut — a uniform `1px solid rgba(255,255,255,.5)` plus a
 * bright top inset sheen — inverts that lighting and reads as a hard white
 * line across the top edge, which is what this replaces.
 */
const RIM_SIDES =
  '-1px 0 0 rgba(208, 208, 208, 0.55), 1px 0 0 rgba(208, 208, 208, 0.55)';
/** Light from below, faint shade above — Figma's inner-shadow pair. */
const RIM_MODEL =
  'inset 0 -14px 22px -12px rgba(255, 255, 255, 0.55), inset 0 10px 14px -10px rgba(40, 40, 40, 0.07)';
/**
 * A side-only rim leaves the top and bottom edges undrawn, which is invisible
 * on a white page. This closes the outline with a neutral DARK hairline —
 * dark, not white, so it reads against white without re-creating the bright
 * top border this recipe exists to avoid. It disappears over dark backdrops,
 * where the fill and blur already separate the button.
 */
const RIM_EDGE = '0 0 0 1px rgba(17, 24, 39, 0.10)';
/** Real lift, so the plate sits above a flat page rather than melting into it. */
const RIM_LIFT =
  '0 6px 16px -4px rgba(16, 24, 40, 0.14), 0 2px 5px -2px rgba(16, 24, 40, 0.10)';

/**
 * Glass fill. Figma layers black @ 0.25 UNDER white @ 0.25, so its glass sits
 * darker than the page (~#CFCFCF over white). A lone `rgba(255,255,255,a)`
 * composites to pure white on a white background and vanishes, so the neutral
 * underlay is kept: the black layer paints over the white one (CSS paints the
 * first background layer on top), darkening on light pages while the white
 * layer still lifts the button over photos and dark surfaces.
 */
const glassFill = (white: number, dark: number) =>
  `linear-gradient(rgba(0, 0, 0, ${dark}), rgba(0, 0, 0, ${dark})), rgba(255, 255, 255, ${white})`;

const GLASS_RIM = `${RIM_EDGE}, ${RIM_SIDES}, ${RIM_LIFT}, ${RIM_MODEL}`;

/**
 * The secondary button's volume (RIM_MODEL + RIM_LIFT), for the tinted glass
 * of the primary button and the selected period toggle. `tintedGlassSurface`
 * alone only draws 1px edge highlights, which reads as a flat sheet. White
 * shows less over a saturated fill than over grey, so the shading is a touch
 * stronger than RIM_MODEL's.
 */
const TINTED_DEPTH = [
  'inset 0 -16px 22px -12px rgba(255, 255, 255, 0.4)',
  'inset 0 10px 14px -10px rgba(0, 0, 0, 0.12)',
  RIM_LIFT
].join(', ');
const GLASS_RIM_HOVER = `${RIM_EDGE}, ${RIM_SIDES}, 0 8px 20px -4px rgba(16, 24, 40, 0.18), 0 2px 6px -2px rgba(16, 24, 40, 0.12), ${RIM_MODEL}`;

/**
 * Legacy grey gradient + border pair, used only by the `contained` Button
 * variant below. Used to be authored per-partner in `tokens/surfaces.ts`
 * (`bespoke.gradient.grey` / `bespoke.borderImageSource.white`) — every
 * partner's light-mode value was identical, and 5 of 6 partners' dark-mode
 * value was STILL the light-mode value, copy-pasted, never tuned. Only
 * InstaView's dark copy was actually dark-tuned, so that pair is used here
 * for every partner rather than plumbing six duplicated files for one
 * variant nothing else reads.
 */
const LEGACY_GRADIENT_GREY_LIGHT =
  'linear-gradient(95.72deg, rgba(255, 255, 255, 1) 5.26%, rgba(222, 222, 222, 1) 98.93%)';
const LEGACY_GRADIENT_GREY_DARK =
  'linear-gradient(95.72deg, #2E2E2E 5.26%, #1A1A1A 98.93%)';
const LEGACY_BORDER_WHITE_LIGHT =
  'linear-gradient(104.96deg, rgba(231, 231, 231, 1) 2.27%, hsla(0, 0%, 100%, 1.00) 60.54%)';
const LEGACY_BORDER_WHITE_DARK =
  'linear-gradient(104.96deg, #3E3E3E 2.27%, #5A5A5A 60.54%)';

/**
 * `borderImageSource.primary`, derived from a product's own ramp instead of
 * a hand-written hex pair. Used to be authored per-partner in
 * `tokens/surfaces.ts`; 5 of 6 partners had already migrated to exactly this
 * formula (buildGradient's 176deg swapped for 142.22deg) — this is that same
 * formula, finishing the migration for the one partner (InstaView) still on
 * the old hardcoded value. Used by FeatureCard/featureV2.tsx.
 *
 * Takes `PaletteColor` (what `theme.palette.<family>` is typed as) rather
 * than `TColorFamily`: they're the same object at runtime — every family is
 * built from a full 11-step ramp — but MUI's own `PaletteColor` augmentation
 * only guarantees a numeric index signature, not the specific 50-950 keys
 * `TColorRamp` names, so the ramp steps buildGradient reads are asserted
 * here rather than re-typing every palette family call site.
 */
export const primaryBorderImageSource = (family: PaletteColor) =>
  buildGradient(family as unknown as TColorRamp).replace('176deg', '142.22deg');

/**
 * The Liquid Glass surface, shared.
 *
 * Figma builds the secondary button, the plan card and the "SAVE %" badge from
 * an identical BG (black @ .25 under white @ .25, the same ±1.25px #D0D0D0
 * side rim, GLASS + the light-from-below inner shadows). This is that recipe,
 * so a component can take the surface without re-deriving it.
 *
 * It sets only paint — no padding, radius or size — so it can be dropped onto
 * an existing element without changing its box.
 */
export const liquidGlassSurface = ({
  white = 0.3,
  dark = 0.05,
  blur = 20,
  saturate = 180
}: {
  white?: number;
  dark?: number;
  blur?: number;
  saturate?: number;
} = {}) => ({
  background: glassFill(white, dark),
  backdropFilter: `blur(${blur}px) saturate(${saturate}%)`,
  WebkitBackdropFilter: `blur(${blur}px) saturate(${saturate}%)`,
  border: 'none',
  boxShadow: GLASS_RIM
});

/** Tertiary sits lighter: same geometry, softer rim and lift. */
const GLASS_RIM_SOFT = [
  '0 0 0 1px rgba(17, 24, 39, 0.07)',
  '-1px 0 0 rgba(208, 208, 208, 0.35)',
  '1px 0 0 rgba(208, 208, 208, 0.35)',
  '0 4px 12px -3px rgba(16, 24, 40, 0.10)',
  'inset 0 -12px 18px -12px rgba(255, 255, 255, 0.45)'
].join(', ');

const buttonBase = (theme: Theme) => ({
  minHeight: 52,
  padding: '8px 24px',
  borderRadius: theme.radius.full,
  gap: '6px',
  fontFamily: theme.typography.actionLarge.fontFamily,
  fontSize: theme.typography.actionLarge.fontSize,
  fontWeight: theme.typography.actionLarge.fontWeight,
  lineHeight: theme.typography.actionLarge.lineHeight,
  letterSpacing: 0,
  textTransform: 'none' as const
});

/**
 * Shared box for both halves of the Monthly/Yearly toggle (see
 * `periodActive` / `periodInactive` below) — layout only, no colour.
 *
 * `width: 50%` silently becomes MUI's 64px min-width floor whenever the
 * row's parent has no resolved width, which overflows the content and wraps
 * the price. Sharing the row with flex-basis 0 is immune to that, and
 * minWidth:0 lets a half shrink below its content.
 */
const PERIOD_ITEM_BASE = (theme: Theme) => ({
  flex: '1 1 0',
  minWidth: 0,
  minHeight: 61,
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
  alignItems: 'center',
  whiteSpace: 'normal' as const,
  textAlign: 'center' as const,
  textTransform: 'none' as const,
  // Figma "Plan payment period items": 10/8 padding, 8px radius (dev-mode
  // CSS, confirmed against both the light and dark inspected nodes). The 8px
  // sides matter beyond matching the spec — MUI's default 16px leaves
  // ~135px of content on a 375px screen for a price plus badge that need
  // ~149px, which is what wraps them onto extra lines.
  //
  // Briefly tried `radius.full` (a true pill) for the Liquid Glass restyle —
  // reverted: these aren't meant to be pill-shaped, just softly rounded, and
  // a full pill's curved end-caps don't sit well under the directional
  // (vertical-only) inset top highlight below — it reads as a clean line on
  // a gently-rounded rectangle but shows a visible seam where a pill's flat
  // edge breaks into its curve.
  padding: '10px 8px',
  borderRadius: '8px',
  boxShadow: theme.palette.shadows.glass
});

/**
 * One glass recipe at three weights — the FALLBACK surface.
 *
 * `opacity` is how much white (light) / neutral (dark) sits over the
 * backdrop: raising it trades the glass effect for text contrast. Light and
 * dark take separate numbers — not one shared value — because they need
 * genuinely different weights to read as "glass" rather than "flat card":
 * light composites toward white, so it can sit fairly heavy (~0.45-0.6) and
 * still look frosted; the same weight in dark reads as a flat grey panel,
 * so dark needs to sit much lower (~0.1-0.18) for the backdrop to actually
 * show through. Pass one number to weight both modes alike, matching the
 * old single-value call sites this replaced.
 *
 * The hairline and inner highlight are what give the edge its lift — without
 * them the card reads as flat translucency.
 *
 * The rgba fill is deliberate rather than relying on backdrop-filter alone:
 * where the filter is unsupported (older Android WebView) the card still
 * renders as a legible translucent surface instead of vanishing — the same
 * role a `@supports (backdrop-filter: blur(1px))` fallback would play, just
 * expressed as "the fill is the base layer, blur is additive" rather than a
 * separate unsupported-browser branch.
 */
/**
 * FIRST PASS, pending design review: the Glass spec only covers light mode,
 * so the dark recipe below is this file's own reasonable default, not a
 * transcribed spec. Only the tint/highlight/ring colours differ by mode —
 * the structure (backdrop blur, hairline, inset highlight, outer ring, drop
 * shadow) is identical either way.
 */
/** Resolves a single-number-or-per-mode opacity down to the active mode's weight. */
const modeWeight = (
  dark: boolean,
  opacity: number | { light: number; dark: number }
) => {
  if (typeof opacity === 'number') return opacity;
  return dark ? opacity.dark : opacity.light;
};

export const glassSurface = (
  theme: Theme,
  opacity: number | { light: number; dark: number },
  /**
   * Overrides dark mode's default tint (`background.paper`). A big card
   * reads as "raised" against the near-black page by staying a dark neutral
   * — a small disc (an icon chip) needs the opposite: white-over-dark is
   * what makes it read as a clearly defined, separate circle rather than
   * barely differentiating from the page it sits on, since dark-grey-over-
   * dark-page barely changes lightness no matter the opacity. Pass
   * `'#FFFFFF'` for that case; omit for the default card/toggle behaviour.
   */
  darkTintOverride?: string
) => {
  const dark = theme.palette.mode === 'dark';
  const weight = modeWeight(dark, opacity);

  // Light composites toward white — the Glass spec's frosted look. Dark has
  // no spec, so it composites toward `background.paper` (this partner's own
  // dark neutral) instead of another shade of black, so a card still reads
  // as "raised" on the near-black page rather than disappearing into it —
  // unless overridden (see `darkTintOverride` above).
  const tint = dark
    ? (darkTintOverride ?? theme.palette.background.paper)
    : '#FFFFFF';
  const edgeOpacity = Math.min(weight * 0.4 + 0.08, 0.3);

  // ONE edge layer, not two. This used to be a white `border` (often close
  // to invisible on a pale/tinted light-mode page — measures as little as
  // 1.04:1) PLUS a separate dark `box-shadow` ring stacked at the same
  // offset specifically to compensate for that. Two layers meant to draw
  // one edge, at a very slightly different offset, reads as a doubled /
  // overlapping line once the fill is genuinely translucent instead of the
  // old near-opaque 0.68 — reported live, and still visible in light mode
  // even after dropping the ring for dark mode only (where the border alone
  // already works). Fix is structural, not per-mode: light gets a dark
  // border directly (what the ring used to supply on its own), dark keeps
  // the white-ish border that was already sufficient by itself.
  const border = dark
    ? `0.5px solid ${alpha('#FFFFFF', Math.min(edgeOpacity, 0.7))}`
    : '0.5px solid rgba(0, 0, 0, 0.12)';

  return {
    backgroundColor: alpha(tint, weight),
    backgroundImage: 'none',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border,
    boxShadow: [
      // Top highlight, dimmer in dark mode — a hard white inset line reads
      // artificial against a dark surface. Light mode's own value used to be
      // 0.6 at a tight 1px blur — strong and crisp enough to trace almost
      // the whole rounded-rect perimeter, not just the top edge, which is
      // what read as a second border concentric with the real one (visible
      // in a zoomed screenshot, confirmed clean without it). 0.35 at a
      // softer 2px blur keeps the sheen without it reading as a ring.
      `inset 0 1px 2px ${alpha('#FFFFFF', dark ? 0.08 : 0.35)}`,
      `inset 0 -1px 1px rgba(0, 0, 0, ${dark ? 0.2 : 0.04})`,
      theme.palette.shadows.card
    ].join(', '),
    color: theme.palette.text.primary
  };
};

/**
 * The "active glass" treatment for a selected/highlighted state — the
 * yearly toggle half, the picked plan-tier card — where a flat product-colour
 * fill would read as a different, heavier material than its own unselected
 * glass sibling. Same recipe as `glassSurface`, tinted toward
 * `theme.palette.primary.main` instead of white/neutral, with a brighter
 * primary-coloured edge and an outer glow standing in for "this is the one
 * that's on" instead of a hard fill swap.
 */
export const tintedGlassSurface = (
  theme: Theme,
  opacity: number | { light: number; dark: number } = 0.25,
  /**
   * Overrides the default `primary.main` tint — for a caller whose "this
   * is the one" colour isn't the product colour, e.g. the status badge's
   * secondary-accent error state (see IconSurface's `glassStatus`).
   */
  tintOverride?: string
) => {
  const dark = theme.palette.mode === 'dark';
  const weight = modeWeight(dark, opacity);
  const tint = tintOverride ?? theme.palette.primary.main;

  return {
    backgroundColor: alpha(tint, weight),
    backgroundImage: 'none',
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid ${alpha(tint, dark ? 0.55 : 0.65)}`,
    boxShadow: [
      // Same fix as glassSurface's identical highlight: 0.6 at 1px blur
      // traced almost the whole rounded-rect perimeter, reading as a second
      // border concentric with the real one — confirmed in a zoomed
      // screenshot, clean without it.
      `inset 0 1px 2px ${alpha('#FFFFFF', dark ? 0.14 : 0.35)}`,
      `inset 0 -1px 1px rgba(0, 0, 0, ${dark ? 0.2 : 0.04})`,
      // The glow that reads as "active" rather than just "a different fill".
      `0 0 20px ${alpha(tint, dark ? 0.35 : 0.25)}`,
      theme.palette.shadows.card
    ].join(', '),
    color: theme.palette.text.primary
  };
};

/**
 * A gradient rim for a glass card — bright where it'd catch light, fading
 * out elsewhere — standing in for the Figma spec's own D0D0D0 side rim in
 * the fallback surface below (see `liquidGlass`).
 *
 * A flat single-alpha `border` (what `glassSurface` supplies on its own)
 * reads as a plain outline, not glass — reported live: "the border which is
 * somewhat gradient to give the feel of glass is missing." A real CSS
 * gradient `border` doesn't respect `border-radius`, so this uses the
 * two-layer background trick instead: the fill painted to `padding-box`,
 * a gradient painted to `border-box` through a transparent border, both of
 * which DO clip to the radius correctly.
 *
 * The highlight is a 135deg (top-left-to-bottom-right) diagonal, not a
 * straight 180deg top-to-bottom — a vertical gradient puts equal brightness
 * at the same height all the way round the ring, which reads as the
 * highlight living along the WHOLE top edge and the shade along the WHOLE
 * bottom edge, not the corner catch-light effect. Reported live: "shifted
 * to corner more, right now all the shade of it is at the bottom edge."
 * Diagonal instead concentrates the bright point at the top-left corner and
 * the shade at the opposite (bottom-right) corner.
 *
 * Meant to be spread AFTER `glassSurface(...)` — it overrides
 * background/border/shadow, but reconstructs the same inset-highlight
 * formula `glassSurface` already used so that part doesn't regress; only
 * the OUTER drop shadow actually changes here (see below).
 */
const glassCardEdge = (theme: Theme, tint: string, weight: number) => {
  const dark = theme.palette.mode === 'dark';
  const fill = alpha(tint, weight);
  const edgeTop = dark ? alpha('#454545', 0.02) : alpha('#FFFFFF', 0.85);
  const edgeBottom = dark ? alpha('#7e7e7e', 0.3) : alpha('#ffffff', 0.5);
  return {
    backgroundColor: 'transparent',
    backgroundImage: [
      `linear-gradient(${fill}, ${fill})`,
      `linear-gradient(135deg, ${edgeTop} 0%, ${edgeBottom} 100%)`
    ].join(', '),
    backgroundClip: 'padding-box, border-box',
    border: '1px solid transparent',
    // `glassSurface`'s own outer shadow (`theme.palette.shadows.card`,
    // `0 4px 12px rgba(0,0,0,0.1)`) isn't mode-aware — the same faint value
    // in both modes barely registers against an already-dark page, so the
    // card doesn't read as lifted. Dark mode gets a real, darker floating
    // shadow instead; light mode is untouched. The two inset lines are
    // `glassSurface`'s own formula, reconstructed here since this object
    // replaces its `boxShadow` key wholesale rather than merging into it.
    boxShadow: [
      `inset 0 1px 2px ${alpha(fill, dark ? 0.08 : 0.35)}`,
      `inset 0 -1px 1px rgba(0, 0, 0, ${dark ? 0.2 : 0.04})`,
      dark ? '0 10px 28px rgba(0, 0, 0, 0.5)' : theme.palette.shadows.card
    ].join(', ')
  };
};

/**
 * Upgrades a card to the real Liquid Glass surface where the browser can
 * render it, keeping the frosted fallback everywhere else.
 *
 * The Figma spec builds depth from stacked layers and blend modes rather than
 * from a blur — and those blend modes are the catch:
 *
 *   plus-lighter  Safari only, for `background-blend-mode`  used by `regular`/`clear`
 *   plus-darker   Safari only                                used by `tinted`
 *
 * The `plus-lighter`/`plus-darker` *keywords* are broadly supported for
 * `mix-blend-mode` (Chrome included), but NOT for `background-blend-mode` —
 * which is the property `regular`/`clear`'s own `fill` actually uses.
 * Checked live: `CSS.supports('background-blend-mode', 'plus-lighter')` is
 * `false` on current desktop Chrome. So in practice this isn't an
 * old-Android-only fallback — it's what nearly everyone on Chrome sees, and
 * the frosted surface needs to hold up as the real thing, not a degraded
 * backup. `glassCardEdge` above is what makes it read as glass rather than a
 * flat tinted panel.
 *
 * The spec's surfaces are two layers: `fill` on the element and `effect` — an
 * inner sheen along the top and bottom edges — which needs its own overlay,
 * hence ::after.
 */
const liquidGlass = (
  theme: Theme,
  surface: TGlassSurface,
  fallbackOpacity: number,
  blendMode: 'plus-lighter' | 'plus-darker'
) => {
  const dark = theme.palette.mode === 'dark';
  const tint = dark ? theme.palette.background.default : '#FFFFFF';

  return {
    // Base: frosted glass, everywhere.
    ...glassSurface(theme, fallbackOpacity),
    ...glassCardEdge(theme, tint, fallbackOpacity),
    borderRadius: surface.radius,
    position: 'relative' as const,
    // The sheen is clipped to the card's corners.
    isolation: 'isolate' as const,

    [`@supports (background-blend-mode: ${blendMode})`]: {
      ...surface.fill,
      // The spec supplies its own edges and depth; the frosted treatment
      // would otherwise sit on top of it — including glassCardEdge's own
      // background-clip, which must be reset or it clips this single-layer
      // fill to padding-box only and leaves a visible gap at the edge.
      backdropFilter: 'none',
      WebkitBackdropFilter: 'none',
      border: 'none',
      backgroundClip: 'unset',
      '&::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRadius: 'inherit',
        pointerEvents: 'none',
        ...surface.effect
      }
    }
  };
};

/**
 * What a TextField's `.MuiOutlinedInput-root` actually paints as its
 * background, per `data-field-variant` (see MuiTextField below) — the
 * single source both the real fill AND MuiInputBase's browser-autofill
 * cover (further below) read from, so the two can't independently drift
 * out of sync. `outline` / `transparent` have no fill of their own, so
 * `'transparent'` is the honest answer for them too, even on the autofill
 * cover — the alternative was this file guessing at a stand-in colour
 * (`background.default`) that has no guarantee of matching whatever is
 * actually behind the field at any given call site.
 */
const FIELD_BACKGROUND = {
  solid: (theme: Theme) => theme.palette.background.paper,
  outline: () => 'transparent',
  transparent: () => 'transparent'
} as const;

export const componentOverrides: Components<Omit<Theme, 'components'>> = {
  MuiCssBaseline: {
    styleOverrides: {
      // Resolved per partner from partners/{partner}/typography.ts so the
      // <body> default follows the active brand instead of a fixed stack.
      body: ({ theme }: { theme: Theme }) => ({
        fontFamily: theme.typography.baselineFontFamily
      })
    }
  },
  MuiLink: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        cursor: 'pointer',
        textDecoration: 'none',
        lineHeight: '16px',
        transition: 'all 0.1s ease-in-out',
        color: theme.palette.primary.main,
        '&:hover': {
          opacity: 0.8,
          color: theme.palette.primary.dark
        }
      })
    }
  },
  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        // Translucent ink rather than a solid grey. A fixed colour cannot suit
        // every ground this lands on: `background.skeleton` was #F0F0F0, which
        // reads as a foreign grey block on the product-tinted panels and glass
        // popups, and in dark mode it resolved to #2B2B2B - the exact colour of
        // `background.paper`, so the skeleton was invisible against the card it
        // stood in for. Derived from `text.primary`, it is always one shade
        // away from whatever is behind it, in either mode.
        backgroundColor: alpha(theme.palette.text.primary, 0.08)
      }),
      // Skeletons stand in for cards and rows, so give them the same corner as
      // the surfaces they replace; call sites that stand in for a pill still
      // override this with radius.full.
      rounded: ({ theme }: { theme: Theme }) => ({
        borderRadius: theme.radius.md
      }),
      pulse: {
        animation: 'pulseAnimation 0.8s ease-in-out infinite',
        '@keyframes pulseAnimation': {
          '0%': { opacity: 0.6 },
          '50%': { opacity: 0.8 },
          '100%': { opacity: 0.6 }
        }
      },
      wave: {
        animation: 'waveAnimation 1s linear infinite',
        '@keyframes waveAnimation': {
          '0%': { transform: 'translateX(-100%)' },
          '100%': { transform: 'translateX(100%)' }
        }
      }
    }
  },
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        aspectRatio: '1/1',
        color: theme.palette.text.primary
      })
    }
  },
  MuiButton: {
    variants: [
      {
        // Primary (Figma: Tinted glass). Solid product colour + a top-down
        // sheen; follows the active product because withProduct() re-points
        // `primary`. Text is the family's readable contrast colour.
        //
        // `color="secondary"` swaps the fill for the palette's secondary
        // accent instead of the product colour — failure CTAs (StatusPopup's
        // error variant) shouldn't read as the product's brand action.
        // `color="error"` goes further, for destructive confirmations
        // (deleting a card) where the CTA has to read as a warning.
        props: { variant: 'primary' },
        style: ({
          theme,
          ownerState
        }: {
          theme: Theme;
          // Optional only to satisfy MUI's `variants[].style` type, which
          // doesn't declare `ownerState` even though it's always passed at
          // runtime.
          ownerState?: ButtonProps;
        }) => {
          // Same "active glass" surface as the selected Monthly/Yearly toggle
          // (`periodActive` below) — translucent tinted fill, blur, tinted
          // edge and glow — at the same 0.82 weight, since the label is
          // `contrastText`, tuned for a near-opaque fill. The old flat fill
          // read as a solid pill next to the toggle's glass.
          //
          // MUI's base `&:hover` overrides background-COLOR (and blanks it on
          // touch via `@media (hover: none)`), so the fill is re-declared in
          // every interactive state.
          // `error` is the destructive slot — delete / remove confirmations,
          // which must not read as the product's brand action nor as the
          // softer `secondary` accent.
          const paletteFor = {
            secondary: theme.palette.secondary,
            error: theme.palette.error
          } as const;
          const palette =
            paletteFor[ownerState?.color as keyof typeof paletteFor] ??
            theme.palette.primary;
          const tinted = tintedGlassSurface(theme, 0.82, palette.main);
          const surface = {
            ...tinted,
            boxShadow: `${tinted.boxShadow}, ${TINTED_DEPTH}`
          };
          const text = palette.contrastText;
          return {
            ...buttonBase(theme),
            ...surface,
            cursor: 'pointer',
            color: text,
            transition: 'filter 0.15s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:hover': {
              ...surface,
              color: text,
              filter: 'brightness(1.06)',
              '@media (hover: none)': { ...surface, color: text }
            },
            '&:active': { ...surface, color: text, filter: 'brightness(0.94)' },
            // Disabled keeps the fill, just faded — a grey/white disabled
            // state is indistinguishable from the secondary button.
            '&.Mui-disabled': {
              ...surface,
              color: text,
              opacity: 0.45,
              boxShadow: 'none',
              cursor: 'not-allowed'
            }
          };
        }
      },
      {
        // Secondary (Figma: Regular glass). Web "liquid glass": a translucent
        // fill over a SATURATED backdrop blur, with a bright rim — so it
        // refracts the page behind it instead of reading as a flat grey pill.
        // Explicit :hover/:active stop MUI injecting a white hover background
        // (which showed through the translucent fill and hid the label).
        props: { variant: 'secondary' },
        style: ({ theme }: { theme: Theme }) => {
          const base = glassFill(0.3, 0.05);
          return {
            ...buttonBase(theme),
            background: base,
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            // No border: Figma's secondary button carries no stroke. Its edge
            // is a pair of grey side shadows (a lens rim — light passes through
            // the sides, so the top and bottom stay open) plus an ambient lift.
            // A uniform white 1px border reads as a hard line across the top.
            border: 'none',
            cursor: 'pointer',
            color: theme.palette.text.primary,
            boxShadow: GLASS_RIM,
            transition: 'background 0.15s ease, box-shadow 0.15s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:hover': {
              background: glassFill(0.45, 0.03),
              boxShadow: GLASS_RIM_HOVER,
              // MUI blanks the background on touch devices; keep the glass.
              '@media (hover: none)': { background: base }
            },
            '&:active': { background: glassFill(0.22, 0.07) },
            // Was a flat, non-mode-aware ~12% white with no rim at all —
            // dropping the whole glass fill/edge read as the button
            // vanishing rather than just going inactive. Keeps the same
            // glass fill and rim as every other state, just dimmed via
            // opacity, matching how the primary/error variant's own
            // disabled state stays legible (see above).
            '&.Mui-disabled': {
              background: base,
              boxShadow: GLASS_RIM,
              opacity: 0.45,
              color: theme.palette.text.disabled,
              cursor: 'not-allowed'
            }
          };
        }
      },
      {
        // Tertiary (Figma: Clear glass). Same recipe, most transparent.
        props: { variant: 'tertiary' },
        style: ({ theme }: { theme: Theme }) => {
          const base = glassFill(0.16, 0.035);
          return {
            ...buttonBase(theme),
            background: base,
            backdropFilter: 'blur(16px) saturate(160%)',
            WebkitBackdropFilter: 'blur(16px) saturate(160%)',
            border: 'none',
            cursor: 'pointer',
            color: theme.palette.text.primary,
            boxShadow: GLASS_RIM_SOFT,
            transition: 'background 0.15s ease',
            WebkitTapHighlightColor: 'transparent',
            '&:hover': {
              background: glassFill(0.28, 0.02),
              '@media (hover: none)': { background: base }
            },
            '&:active': { background: glassFill(0.1, 0.05) },
            '&.Mui-disabled': {
              background: 'rgba(255, 255, 255, 0.06)',
              color: theme.palette.text.disabled,
              boxShadow: 'none',
              cursor: 'not-allowed'
            }
          };
        }
      },
      {
        // Chromeless text button — no fill, no border, no lift. For low-
        // emphasis inline actions ("View all", "Edit") that sit next to a
        // primary CTA without competing with it.
        props: { variant: 'ghost' },
        style: ({ theme }: { theme: Theme }) => ({
          '&.Mui-disabled': {
            borderImageSource: 'none',
            cursor: 'not-allowed',
            color: theme.palette.text.disabled
          },
          '&.Mui-hover': {
            opacity: 0.8
          }
        })
      },
      {
        // Tonal action chip — a soft product-tinted fill derived from
        // `primary.main`, so it stays one step off whatever surface it sits
        // on (white card or tinted panel) and follows the active product.
        // Used for small inline row actions ("Change", "Download", "Invoices").
        props: { variant: 'tonal' },
        style: ({ theme }: { theme: Theme }) => ({
          minWidth: 0,
          flexShrink: 0,
          padding: '6px 14px',
          borderRadius: theme.radius.full,
          fontFamily: theme.typography.bodyMedium.fontFamily,
          fontSize: theme.typography.bodyMedium.fontSize,
          fontWeight: 500,
          lineHeight: theme.typography.bodyMedium.lineHeight,
          letterSpacing: 0,
          textTransform: 'none' as const,
          border: 'none',
          boxShadow: 'none',
          color: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.12),
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            boxShadow: 'none'
          },
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            backgroundColor: alpha(theme.palette.text.disabled, 0.12),
            boxShadow: 'none'
          }
        })
      },
      {
        // Small pill (Figma "Buttons" 24px inline size — Retry payment /
        // Update card / Download / Change / Open Cash App). Layout only, so it
        // composes with any fill variant above: `variant="secondary"
        // size="small"` is the glass pill, `variant="primary" size="small"`
        // the filled one. 13/600 label, 16px leading icon.
        props: { size: 'small' },
        style: ({ theme }: { theme: Theme }) => ({
          minHeight: 0,
          minWidth: 0,
          flexShrink: 0,
          padding: '8px 12px',
          gap: '4px',
          fontFamily: theme.typography.bodySmall.fontFamily,
          fontSize: theme.typography.bodySmall.fontSize,
          lineHeight: theme.typography.bodySmall.lineHeight,
          fontWeight: 600,
          whiteSpace: 'nowrap' as const,
          '& .MuiButton-startIcon': {
            margin: 0,
            '& > *:nth-of-type(1)': { fontSize: 16 }
          }
        })
      },
      {
        // Legacy grey gradient button, kept for the few screens still on it
        // (ErrorBoundary, Support). Prefer `secondary` for new work — this
        // reads as a pre-Glass control. Moved here from an `ownerState`
        // branch so it is a declared variant like the rest.
        props: { variant: 'contained' },
        style: ({ theme }: { theme: Theme }) => ({
          padding: theme.spacing(2.5),
          fontSize: theme.typography.bodyLarge.fontSize,
          fontWeight: theme.typography.bodyLarge.fontWeight,
          lineHeight: theme.typography.bodyLarge.lineHeight,
          textTransform: 'capitalize' as React.CSSProperties['textTransform'],
          background: LEGACY_GRADIENT_GREY_LIGHT,
          borderStyle: 'solid !important',
          borderWidth: '1px !important',
          borderRadius: theme.radius.xl,
          borderImageSource: LEGACY_BORDER_WHITE_LIGHT,
          boxShadow: theme.palette.shadows.lightBlack,
          color: theme.palette.text.primary,
          ...theme.applyStyles('dark', {
            background: LEGACY_GRADIENT_GREY_DARK,
            borderImageSource: LEGACY_BORDER_WHITE_DARK
          }),
          '&.Mui-disabled': {
            color: theme.palette.text.disabled,
            boxShadow: 'none',
            borderImageSource: 'none'
          }
        })
      },
      {
        // Figma "Plan payment period items" — the filled half of the
        // Monthly/Yearly toggle on the plan-comparison screen. Was a flat
        // `primary.main` fill; now the same "active glass" tint every other
        // selected state uses, so the toggle and the plan cards read as one
        // material. Opacity sits high (0.82) rather than at the usual tinted
        // weight (~0.25) — this fill sits directly under `primary.contrastText`,
        // which is tuned for a fully opaque `primary.main`, so the fill stays
        // close to that colour rather than trading contrast for translucency.
        props: { variant: 'periodActive' },
        style: ({ theme }: { theme: Theme }) => {
          const tinted = tintedGlassSurface(theme, 0.82);
          const surface = {
            ...tinted,
            boxShadow: `${tinted.boxShadow}, ${TINTED_DEPTH}`
          };
          return {
            ...PERIOD_ITEM_BASE(theme),
            ...surface,
            color: theme.palette.primary.contrastText,
            transition: 'transform 150ms ease, filter 150ms ease',
            // Keep the surface on every interaction state — MUI's own
            // unconditional `:hover` rule sets `background-color` too, and
            // since it's a plain `:hover` selector (same specificity as
            // ours), it wins on any property we don't also redeclare here.
            // `:hover`/`:active` used to only set `filter`/`transform`,
            // which left MUI's default hover fill (a near-black overlay)
            // as the only rule actually setting `background-color` during
            // a hover-without-focus — reported live as the selected toggle
            // "losing color" on interaction.
            '&:hover': {
              ...surface,
              color: theme.palette.primary.contrastText,
              filter: 'brightness(1.05)'
            },
            '&:active': {
              ...surface,
              color: theme.palette.primary.contrastText,
              transform: 'scale(0.98)',
              filter: 'brightness(0.96)'
            },
            '&:focus': {
              ...surface,
              color: theme.palette.primary.contrastText
            }
          };
        }
      },
      {
        // The unfilled half of the same toggle. Same frosted-glass recipe as
        // the plan-tier cards (`planTier` below) rather than its own bespoke
        // fill/border — one surface, reused, instead of a second one-off
        // translucency recipe to keep in sync. Fixes a real bug too: the
        // previous bespoke version faked a gradient stroke with a second,
        // opaque `background` layer clipped to `border-box`, on the theory
        // that the translucent fill on top would only let it show through a
        // thin ring; `background-clip: border-box` doesn't confine painting
        // that way, it covers the whole box, so the toggle rendered as a
        // solid off-white card instead of translucent glass — worst on dark
        // pages. `glassSurface` draws its edge with a real `border`, which
        // has no such failure mode.
        //
        // Weight dropped from a shared 0.68 (the "nearly solid" tier, chosen
        // for `glassSolid` where a busy backdrop needs the coverage) to a
        // genuinely translucent, per-mode pair — light stays fairly present
        // (0.5) since it composites toward white and gets washed out below
        // that; dark drops much further (0.14), since the same weight there
        // reads as a flat grey panel instead of glass.
        props: { variant: 'periodInactive' },
        style: ({ theme }: { theme: Theme }) => {
          const surface = glassSurface(theme, { light: 0.5, dark: 0.14 });
          return {
            ...PERIOD_ITEM_BASE(theme),
            ...surface,
            transition: 'transform 150ms ease, filter 150ms ease',
            '&:hover': { ...surface, filter: 'brightness(1.08)' },
            '&:active': {
              ...surface,
              transform: 'scale(0.98)',
              filter: 'brightness(0.95)'
            },
            '&:focus': surface
          };
        }
      }
    ],
    styleOverrides: {
      root: {
        textTransform: 'none' as React.CSSProperties['textTransform'],
        // The variants supply their own focus treatment (rings, fills); the
        // browser's default outline would sit on top of it. Was a `styled()`
        // wrapper in coreComponents/Button — moved here so a bare <Button>
        // behaves the same.
        '&:focus, &:focus-visible': { outline: 'none' }
      }
    }
  },
  // The app's three field looks, all in one place. `coreComponents/TextField`
  // is a thin wrapper that only sets `data-field-variant` (it always renders
  // MUI's `outlined` input, since MUI's own `variant` prop is reserved). A
  // bare <TextField variant="outlined"> gets the `solid` look too.
  //
  //  - solid        white/paper fill + soft shadow, no border (the default,
  //                 Figma "Camera Paired/No one" → Content Area: 343x52,
  //                 20px side padding, no notched outline)
  //  - outline      transparent fill + a 1px themed border
  //  - transparent  no fill, no radius — a bottom hairline that turns the
  //                 product colour on focus
  MuiTextField: {
    styleOverrides: {
      root: ({
        ownerState,
        theme
      }: {
        ownerState: TextFieldProps;
        theme: Theme;
      }) => {
        if (ownerState.variant !== 'outlined') return {};

        const border = theme.palette.border.default;
        const warn = theme.palette.warning.main;
        const primary = theme.palette.primary.main;

        return {
          width: '100%',

          // ---- shared box (all looks) + the `solid` surface as the default
          '& .MuiOutlinedInput-root': {
            height: 52,
            padding: '0 20px',
            borderRadius: theme.radius.md,
            // Roboto 400 at 18px — the field's own size, between bodyLarge
            // (16) and the 18px title/action variants (which are Inter 600).
            fontFamily: theme.typography.bodyLarge.fontFamily,
            fontWeight: theme.typography.bodyLarge.fontWeight,
            fontSize: '1.125rem',
            lineHeight: '24px',
            color: theme.palette.text.primary,
            transition: 'box-shadow .15s ease, border-color .15s ease',
            border: 'none',
            background: FIELD_BACKGROUND.solid(theme),
            boxShadow: theme.palette.shadows.card,
            // Focus ring drawn on the rounded root (box-shadow follows the
            // border-radius) — the native outline sits on the inner <input>,
            // which has no radius and would render as a rectangle.
            '&.Mui-focused': {
              boxShadow: `${theme.palette.shadows.card}, 0 0 0 2px ${alpha(
                primary,
                0.35
              )}`
            },
            '&.Mui-error': {
              boxShadow: `${theme.palette.shadows.card}, inset 0 0 0 1px ${warn}`
            },
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
              boxShadow: 'none',
              opacity: 0.6
            }
          },
          // No notched outline in any look — the surface draws the edge.
          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
          // Multiline fields grow with their content: the fixed 52px height
          // above would let wrapped text spill out of the box.
          '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
            height: 'auto',
            minHeight: 52,
            padding: 0
          },
          // Padding lives on the textarea itself so the caret and text are
          // inset inside the element rather than clipped at its edge.
          '& .MuiOutlinedInput-root.MuiInputBase-multiline textarea.MuiOutlinedInput-input':
            { height: 'auto', padding: '14px 20px' },
          '&[data-field-variant="transparent"] .MuiOutlinedInput-root.MuiInputBase-multiline textarea.MuiOutlinedInput-input':
            { padding: '0 0 4px' },
          '& .MuiOutlinedInput-input': {
            padding: 0,
            height: '100%',
            borderRadius: 'inherit',
            '&:focus': { outline: 'none' },
            // Neutral grey, not `text.tertiary` — that token is the brand's
            // tertiary *text style* (e.g. an inline warning), not a chrome
            // colour, and the two can diverge (tertiary is red for several
            // partners). `gray[600]` is the closest ramp step to the #999
            // placeholder this field always shipped with.
            '&::placeholder': { color: theme.palette.gray[600], opacity: 1 }
          },
          '& .MuiFormHelperText-root': {
            fontFamily: theme.typography.labelSmall.fontFamily,
            fontSize: theme.typography.labelSmall.fontSize,
            fontWeight: theme.typography.labelSmall.fontWeight,
            lineHeight: theme.typography.labelSmall.lineHeight,
            margin: '4px 20px 0',
            '&.Mui-error': { color: warn }
          },

          // ---- outline: transparent fill + a 1px border
          '&[data-field-variant="outline"] .MuiOutlinedInput-root': {
            background: FIELD_BACKGROUND.outline(),
            boxShadow: 'none',
            border: `1px solid ${border}`,
            '&.Mui-focused': {
              borderColor: primary,
              boxShadow: `0 0 0 2px ${alpha(primary, 0.2)}`
            },
            '&.Mui-error': { borderColor: warn, boxShadow: 'none' },
            '&.Mui-disabled': { boxShadow: 'none' }
          },

          // ---- transparent: just a bottom hairline
          '&[data-field-variant="transparent"] .MuiOutlinedInput-root': {
            background: FIELD_BACKGROUND.transparent(),
            boxShadow: 'none',
            border: 'none',
            borderBottom: `1px solid ${border}`,
            borderRadius: 0,
            padding: '0 0 4px',
            '&.Mui-focused': {
              boxShadow: 'none',
              borderBottomColor: primary
            },
            '&.Mui-error': { boxShadow: 'none', borderBottomColor: warn }
          },
          '&[data-field-variant="transparent"] .MuiFormHelperText-root': {
            margin: '4px 0 0'
          }
        };
      }
    }
  },

  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        fontSize: theme.typography.bodyLarge.fontSize,
        fontWeight: theme.typography.bodyLarge.fontWeight,
        lineHeight: theme.typography.bodyLarge.lineHeight,
        color: theme.palette.text.contrastText
      })
    }
  },
  MuiDrawer: {
    styleOverrides: {
      // The drawer surface follows the page ground (product-tinted) and drops
      // MUI's default elevation gradient image; individual drawers add their
      // own border / radius / shadow.
      root: ({ theme }: { theme: Theme }) => ({
        color: theme.palette.text.primary
      }),
      paper: ({ theme }: { theme: Theme }) => ({
        backgroundColor: theme.palette.background.default,
        backgroundImage: 'none',
        color: theme.palette.text.primary
      })
    }
  },
  // Figma (Payment Succeed → Modal): a dimmed, blurred overlay behind a glass
  // dialog. Padding stays with the callers — the design bleeds its illustration
  // to the top edge, which not every popup does.
  // MUI only maps its own variant names to elements; anything custom falls back
  // to <span>, so two stacked Typographies rendered on one line. Text variants
  // map to block elements; action/label stay inline (button and badge text).
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        titleLarge: 'h1',
        titleMedium: 'h2',
        titleSmall: 'h3',
        titleExtraSmall: 'h4',
        bodyLarge: 'p',
        bodyMedium: 'p',
        bodySmall: 'p',
        messageLarge: 'p',
        messageSmall: 'p',
        actionLarge: 'span',
        actionMedium: 'span',
        labelSmall: 'span'
      }
    }
  },
  MuiBackdrop: {
    styleOverrides: {
      // Menu, Select, Popover and Autocomplete are Modals too, and render an
      // `invisible` backdrop. MUI's own invisible style only clears the
      // background colour, so an unscoped blur here would frost the page
      // behind every dropdown. Only the visible scrim gets the glass.
      root: ({ theme }: { theme: Theme }) => ({
        '&:not(.MuiBackdrop-invisible)': {
          backgroundColor: alpha(theme.palette.common.black, 0.2),
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)'
        }
      })
    }
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }: { theme: Theme }) => {
        // FIRST PASS, pending design review. Weight dropped repeatedly,
        // live — 0.85/0.5 read as too opaque/solid, not the frosted
        // "Glass" material the reference (a shadcn-style GlassAlertDialog)
        // shows; light alone kept dropping further after that (0.5 -> 0.3
        // -> 0.18 -> 0.1) as it still read as a white frost. Landed on one
        // shared weight for both modes. Tint is background.default (the
        // page's own tint, including its soft gradient wash — see
        // PrivateRoute.tsx), not the neutral background.paper, so the
        // popup composites toward whatever's actually behind it, same
        // reasoning as liquidGlass()'s dark tint above.
        const tint = theme.palette.background.default;
        const weight = 0.1;

        return {
          borderRadius: theme.radius.xl,
          // Same white/grey corner-highlight gradient rim the glass card
          // system uses (glassCardEdge) — a plain flat border reads as an
          // outline, not glass; the popup had no border at all before this.
          // `boxShadow` is restored to this dialog's own value right after,
          // since glassCardEdge's own shadow is tuned for a card, not a
          // modal sitting over a blurred, unpredictable backdrop.
          ...glassCardEdge(theme, tint, weight),
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: theme.palette.shadows.sheet,
          // Popup CTAs read one step down from the page's primary button
          // (actionMedium 16px vs actionLarge 18px) — 18px was visually heavy in
          // the narrow dialog. Scoped to the paper so it never touches page
          // buttons.
          '& .MuiButton-root': {
            fontSize: theme.typography.actionMedium.fontSize,
            lineHeight: theme.typography.actionMedium.lineHeight
          }
        };
      }
    }
  },
  MuiDivider: {
    variants: [
      {
        /**
         * The hairline from the Glass token set (GREY_200 -> border.divider).
         *
         * MUI's default already reads `palette.divider`, so this variant
         * exists to name the intent at the call site and to keep the inset
         * and weight consistent wherever a card separates its sections.
         */
        props: { variant: 'hairline' },
        style: ({ theme }: { theme: Theme }) => ({
          border: 0,
          borderBottom: `1px solid ${theme.palette.border.divider}`,
          // Cards on these screens are padded; a full-bleed rule would run
          // past the content it separates.
          marginInline: theme.spacing(2)
        })
      }
    ]
  },

  MuiCard: {
    // Every card gets the Figma surface by default — white, 32px radius, soft
    // drop shadow — so a plain <Card> matches without opting in to a variant.
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        borderRadius: theme.radius.xl,
        backgroundColor: theme.palette.background.paper,
        backgroundImage: 'none',
        boxShadow: theme.palette.shadows.card
      })
    },
    variants: [
      /* ── Liquid Glass surfaces ──────────────────────────────────────────
         The three surfaces the Figma spec defines (themes/tokens/glass.ts),
         each with a frosted fallback for browsers that cannot blend them.

         `clear` -> `regular` -> `tinted` is increasing presence: barely
         there, the workhorse, and the brand-tinted one.                     */
      {
        // Barely there — for cards over an already-busy backdrop, or nested
        // inside another glass surface. Weight dropped from 0.12 — reported
        // live, alongside `glass`/`glassSolid`, as reading too opaque/flat
        // for "barely there".
        props: { variant: 'glassClear' },
        style: ({ theme }: { theme: Theme }) =>
          liquidGlass(theme, theme.palette.glass.clear, 0.09, 'plus-lighter')
      },
      {
        // The workhorse: content cards over a product tint. Weight dropped
        // from 0.25 for the same reason as `glassClear` above.
        props: { variant: 'glass' },
        style: ({ theme }: { theme: Theme }) =>
          liquidGlass(theme, theme.palette.glass.regular, 0.18, 'plus-lighter')
      },
      {
        // Nearly solid. No spec surface matches: this exists for cards whose
        // text would otherwise fall below AA on a busy backdrop, so it stays
        // frosted-only by design — weight stays well above its siblings for
        // that reason, just eased down from 0.68 (reported live as reading
        // like a flat opaque panel rather than glass) to 0.55, still opaque
        // enough to hold AA contrast on a busy backdrop.
        props: { variant: 'glassSolid' },
        style: ({ theme }: { theme: Theme }) => {
          const dark = theme.palette.mode === 'dark';
          const tint = dark ? theme.palette.background.paper : '#FFFFFF';
          return {
            ...glassSurface(theme, 0.55),
            ...glassCardEdge(theme, tint, 0.55),
            borderRadius: theme.radius.xl
          };
        }
      },
      {
        /* The brand-tinted surface. The spec stacks three layers here — an
           opaque white backing, the tint over it, then the sheen — so it
           needs ::before as well as ::after.

           Its blend mode is plus-darker, which only Safari implements, so on
           Android this stays the flat product tint. That fallback is a real
           design in its own right, not a degraded one. */
        props: { variant: 'primaryCard' },
        style: ({ theme }: { theme: Theme }) => {
          const { tinted } = theme.palette.glass;
          return {
            borderRadius: tinted.radius,
            position: 'relative' as const,
            isolation: 'isolate' as const,
            backgroundColor: theme.palette.primary.tintedSurface,
            backgroundImage: 'none',
            border: `0.5px solid ${theme.palette.primary.main}`,
            boxShadow: theme.palette.shadows.card,
            color: theme.palette.text.primary,

            '@supports (mix-blend-mode: plus-darker)': {
              ...tinted.backing,
              border: 'none',
              boxShadow: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                pointerEvents: 'none',
                ...tinted.tintLayer
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                borderRadius: 'inherit',
                pointerEvents: 'none',
                ...tinted.effect
              }
            }
          };
        }
      },
      {
        // The opaque surface — plain paper fill, 32px radius, the card
        // shadow. Same paint as a bare <Card>, named so a call site (and
        // coreComponents/DividedCard) can ask for it explicitly and stay
        // stable if the root default ever moves. Replaces the old
        // `gradientCard` / `gradientCardWithDivider` pair — geometry
        // (padding, gaps, row dividers) now lives on DividedCard, not here.
        props: { variant: 'solid' },
        style: ({ theme }: { theme: Theme }) => ({
          borderRadius: theme.radius.xl,
          backgroundColor: theme.palette.background.paper,
          backgroundImage: 'none',
          border: 'none',
          boxShadow: theme.palette.shadows.card
        })
      },
      /* ── Plan-comparison tier cards ──────────────────────────────────────
         Figma "Plan item": 10px radius. Unselected is the same frosted glass
         as `glassSolid`; selected takes the product's tinted surface with a
         primary hairline. Previously a page-level `styled(Card)` in
         PlanComparison.tsx that ALSO took `variant="glassSolid"` — two
         systems layered on the same element, fighting over `backgroundColor`.
         One named variant per state, owned here, replaces both. */
      {
        // Weight dropped from the "nearly solid" 0.68 tier (right for
        // `glassSolid`'s busy-backdrop case, too opaque for this screen's
        // plain page) to a genuinely translucent, per-mode pair — see
        // `periodInactive`'s identical reasoning above; this is that same
        // surface, applied to the unselected card.
        props: { variant: 'planTier' },
        style: ({ theme }: { theme: Theme }) => ({
          ...glassSurface(theme, { light: 0.5, dark: 0.14 }),
          display: 'flex',
          flexDirection: 'column' as const,
          justifyContent: 'center',
          alignItems: 'center',
          gap: '64px',
          height: '100%',
          padding: 52,
          paddingBottom: 8,
          borderRadius: theme.radius.md,
          WebkitTapHighlightColor: 'transparent',
          transition: 'transform 150ms ease, filter 150ms ease',
          '&:hover': { filter: 'brightness(1.05)' },
          '&:active': { transform: 'scale(0.99)', filter: 'brightness(0.96)' }
        })
      },
      {
        // The picked card — was a flat `primary.tintedSurface` fill with no
        // blur; now the same "active glass" tint `periodActive` uses, so the
        // selected toggle half and the selected card read as one material.
        // `tintedGlassSurface`'s own primary-coloured border and glow already
        // say "this one, in this product's colour" — the old hand-tuned
        // light/dark border override (primary.main vs the less-vivid
        // primary.dark shade) is gone; alpha over the same colour does that
        // toning-down job here instead, one recipe rather than a second
        // bespoke edge to keep in sync.
        props: { variant: 'planTierSelected' },
        style: ({ theme }: { theme: Theme }) => {
          const surface = tintedGlassSurface(theme, {
            light: 0.22,
            dark: 0.22
          });
          return {
            ...surface,
            // tintedGlassSurface's own border is 1px, vs. planTier's (plain
            // glassSurface) 0.5px — the Radio and PromoRibbon inside each
            // card are positioned absolute against THIS card (its nearest
            // `position: relative` ancestor), so that width difference
            // shifted their padding-edge origin by the delta on every
            // select/deselect, on both the newly- and previously-selected
            // card at once. Same width as planTier, keeping only the
            // colour/alpha tintedGlassSurface adds for the "selected" cue.
            border: surface.border.replace(/^1px/, '0.5px'),
            display: 'flex',
            flexDirection: 'column' as const,
            justifyContent: 'center',
            alignItems: 'center',
            gap: '64px',
            height: '100%',
            padding: 52,
            paddingBottom: 8,
            borderRadius: theme.radius.md,
            WebkitTapHighlightColor: 'transparent',
            transition: 'transform 150ms ease, filter 150ms ease',
            '&:hover': { filter: 'brightness(1.05)' },
            '&:active': {
              transform: 'scale(0.99)',
              filter: 'brightness(0.96)'
            }
          };
        }
      }
    ]
  },
  MuiChip: {
    // Figma exposes three chip shapes:
    //  - "Chip"  — selectable pill, 44 tall, Roboto 14. Filled with the
    //    product colour when selected, neutral grey when not. (the default)
    //  - "badge" — tonal status pill, 24 tall, Roboto 13, using a colour's
    //    own ramp (50 background / 500 text): "Current Plan" (tertiary),
    //    "Bundle discount" (secondary). (variant="status")
    //  - "glass badge" — a small frosted pill for a short coloured label:
    //    the plan-comparison "SAVE 17%" callout, and reusable wherever else
    //    a short glass label is needed (e.g. a camera-name chip). Two
    //    variants, not one, because the SAME coloured text needs a
    //    DIFFERENT fill depending on what it sits on:
    //      glassBadge        — neutral grey glass, for a light page, a
    //                          card, or the frosted (`periodInactive`) half
    //                          of a toggle. Figma "Plan payment period
    //                          items" > badge: fill rgba(153,153,153,0.17).
    //      glassBadgeOnFill  — a near-opaque light pill, for sitting on TOP
    //                          of a solid product-colour fill (the active,
    //                          `periodActive` half) — the neutral glass
    //                          above reads as the SAME blue as its own
    //                          `primary`-coloured text once it's stacked on
    //                          a blue button, which is the bug reported
    //                          live: "SAVE %" going unreadable on the
    //                          active side. This isn't a light/dark thing,
    //                          it's which surface the badge is on, so the
    //                          caller picks the variant rather than the
    //                          theme guessing from `palette.mode`.
    variants: [
      {
        props: { variant: 'status' },
        style: ({
          ownerState,
          theme
        }: {
          // Optional only to satisfy MUI's `variants[].style` type, which
          // doesn't declare `ownerState` even though it's always passed.
          ownerState?: ChipProps;
          theme: Theme;
        }) => {
          const key =
            ownerState?.color && ownerState.color !== 'default'
              ? ownerState.color
              : 'primary';
          const family = (theme.palette as any)[key];
          return {
            height: 24,
            borderRadius: theme.radius.full,
            padding: '0 12px',
            border: 'none',
            fontFamily: theme.typography.bodyMedium.fontFamily,
            fontWeight: theme.typography.bodyMedium.fontWeight,
            fontSize: '0.8125rem', // 13px
            backgroundColor: family[50],
            color: family[500],
            '& .MuiChip-label': { padding: 0 }
          };
        }
      },
      ...(['glassBadge', 'glassBadgeOnFill'] as const).map((variant) => ({
        props: { variant },
        style: ({
          ownerState,
          theme
        }: {
          ownerState?: ChipProps;
          theme: Theme;
        }) => {
          // Plain solid text, not a gradient clipped to the label: a page
          // that used `background: <gradient>; background-clip: text` here
          // for the "SAVE %" callout shipped a typo that made the whole
          // `background` value invalid ("...main}.46%..." — a missing
          // space), which silently dropped the gradient and left the label
          // painted in its OTHER declared colour, `transparent` — invisible
          // in both modes. A flat colour can't have that failure mode.
          const key =
            ownerState?.color && ownerState.color !== 'default'
              ? ownerState.color
              : 'primary';
          const family = (theme.palette as any)[key];
          return {
            height: 20,
            borderRadius: theme.radius.full,
            padding: '0 6px',
            border: 'none',
            // `glassBadgeOnFill`'s near-opaque light fill is what keeps the
            // coloured text readable once the badge sits on that SAME
            // colour's own solid fill — `glassBadge`'s neutral grey glass
            // would otherwise read as just another tint of it.
            backgroundColor:
              variant === 'glassBadgeOnFill'
                ? 'rgba(255, 255, 255, 0.85)'
                : 'rgba(153, 153, 153, 0.17)',
            backdropFilter: 'blur(12px) saturate(160%)',
            WebkitBackdropFilter: 'blur(12px) saturate(160%)',
            // A flat grey ring reads as a plain hairline once the pill sits
            // on a dark, already-glass screen — translucent white instead,
            // same edge every other glass surface here uses.
            boxShadow: [
              '0 0 0 0.5px rgba(166, 166, 166, 0.5)',
              '0 4px 12px rgba(0, 0, 0, 0.06)'
            ].join(', '),
            // 0.3, not 0.15 — matches glassSurface()'s own dark-mode edge;
            // 0.15 read as no ring at all against a dark, busy backdrop.
            ...theme.applyStyles('dark', {
              boxShadow: [
                `0 0 0 0.5px ${alpha('#FFFFFF', 0.3)}`,
                '0 4px 12px rgba(0, 0, 0, 0.06)'
              ].join(', ')
            }),
            fontFamily: theme.typography.labelSmall.fontFamily,
            fontWeight: 600,
            fontSize: theme.typography.labelSmall.fontSize,
            textTransform: 'uppercase' as const,
            whiteSpace: 'nowrap' as const,
            color: family.main,
            '& .MuiChip-label': { padding: 0 }
          };
        }
      }))
    ],
    styleOverrides: {
      root: ({
        ownerState,
        theme
      }: {
        ownerState: ChipProps;
        theme: Theme;
      }) => {
        // `status` is handled entirely by the variant above.
        if (ownerState.variant === 'status') return {};

        const key =
          ownerState.color && ownerState.color !== 'default'
            ? ownerState.color
            : 'primary';
        const family = (theme.palette as any)[key];

        return {
          height: ownerState.size === 'small' ? 32 : 44,
          borderRadius: theme.radius.full,
          padding: '0 16px',
          fontFamily: theme.typography.bodyMedium.fontFamily,
          fontWeight: theme.typography.bodyMedium.fontWeight,
          fontSize: theme.typography.bodyMedium.fontSize,
          // Unselected: Figma's neutral grey.
          backgroundColor: '#C4C4C4',
          color: theme.palette.common.black,
          '& .MuiChip-label': { padding: 0 },
          // Selected: follows the active product via `primary`.
          ...(ownerState.color &&
            ownerState.color !== 'default' && {
              backgroundColor: family.main,
              color: family.contrastText
            })
        };
      }
    }
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        color: theme.palette.primary.main,
        '& .MuiSvgIcon-root': {
          fontSize: '1.25rem', // default size
          opacity: 1
        },
        '&.Mui-checked': {
          color: theme.palette.primary.main
        }
      })
    }
  },
  MuiRadio: {
    // The look is drawn entirely below, so a plain <Radio> matches — no
    // wrapper. `default` keeps MUI from layering its own colour classes over
    // the ::before circle; the ripple is replaced by the fill transition.
    defaultProps: {
      color: 'default',
      disableRipple: true
    },
    styleOverrides: {
      // Figma (Atoms → Radio Button, and the plan/payment screens): a 24px
      // circle. Unselected is clear glass with a white hairline; selected is
      // filled with the product colour — no inner dot. Drawn with ::before so
      // it replaces MUI's stacked SVG icons.
      root: ({ theme }: { theme: Theme }) => ({
        padding: 8,
        color: theme.palette.primary.main,
        '& .MuiSvgIcon-root': { display: 'none' },
        '&::before': {
          content: '""',
          display: 'block',
          width: 24,
          height: 24,
          // Fixed size: inside a flex row the circle would otherwise be
          // squeezed horizontally and render as an ellipse.
          minWidth: 24,
          minHeight: 24,
          flexShrink: 0,
          borderRadius: '50%',
          boxSizing: 'border-box',
          // Faint neutral fill (gray[600] @ .17) + a white hairline — same
          // grey the switch off-track and placeholder text use. Not
          // `text.tertiary`: that's the brand's tertiary text style, not a
          // chrome colour, and the two can diverge (tertiary is red for
          // several partners).
          backgroundColor: alpha(theme.palette.gray[600], 0.17),
          // A flat, opaque white hairline reads as "clear glass" on a light
          // page, but on an already-dark, already-glass screen it was the
          // one plain-coloured edge among a bunch of translucent ones —
          // same treatment `glassSurface()` gives its own border.
          border: `1px solid ${theme.palette.common.white}`,
          ...theme.applyStyles('dark', {
            border: `1px solid ${alpha('#FFFFFF', 0.3)}`
          }),
          transition: 'background-color 0.15s ease'
        },
        '&.Mui-checked::before': {
          backgroundColor: theme.palette.primary.main,
          border: 'none'
        },
        '&.Mui-disabled::before': {
          opacity: 0.45
        }
      })
    }
  },

  // On/off toggle — Figma (Atoms → Toggle). A 64x28 pill track: the product
  // colour when on, neutral grey off. The knob is a 38x24 stadium (not a
  // circle), inset 2px, so it travels 22px. The colour follows `primary`,
  // which withProduct() re-points per product. Owned here so a plain <Switch>
  // matches — there is no wrapper component.
  MuiSwitch: {
    defaultProps: {
      disableRipple: true,
      focusVisibleClassName: '.Mui-focusVisible'
    },
    styleOverrides: {
      root: {
        width: 64,
        height: 28,
        padding: 0
      },
      switchBase: ({ theme }: { theme: Theme }) => ({
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
          // 64 - 38 knob - (2 x 2 inset)
          transform: 'translateX(22px)',
          '& + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
            border: 0
          },
          '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.45 }
        },
        '&.Mui-disabled .MuiSwitch-thumb': { opacity: 0.45 },
        '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.45 }
      }),
      thumb: ({ theme }: { theme: Theme }) => ({
        boxSizing: 'border-box',
        width: 38,
        height: 24,
        // A wide stadium knob, per the design — MUI's default thumb is a circle.
        borderRadius: theme.radius.full,
        backgroundColor: theme.palette.common.white,
        boxShadow: theme.palette.shadows.thumb
      }),
      track: ({ theme }: { theme: Theme }) => ({
        borderRadius: theme.radius.full,
        // Neutral off-state — `gray[600]`, the closest ramp step to the #999
        // this switch always shipped with. Not `text.tertiary`: that token is
        // the brand's tertiary text style (red for several partners), which
        // would turn an "off" switch into a warning colour.
        backgroundColor: theme.palette.gray[600],
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
          duration: 300
        })
      })
    }
  },

  // Popover surface — used by MenuPopup's overflow menu and any bare
  // <Popover>. Same glass recipe as MuiDialog's paper (glassCardEdge +
  // backdrop blur, tinted from the page's own background.default rather
  // than a flat neutral) so a kebab menu reads as the same frosted
  // material as every other popup, not a flat solid card. Positioning and
  // trigger state stay with the caller.
  MuiPopover: {
    styleOverrides: {
      paper: ({ theme }: { theme: Theme }) => {
        const tint = theme.palette.background.default;
        const weight = 0.1;
        return {
          borderRadius: theme.radius.sm,
          ...glassCardEdge(theme, tint, weight),
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          boxShadow: theme.palette.shadows.card
        };
      }
    }
  },

  // Base for every MUI text input (TextField / OutlinedInput / Select …). Only
  // job here: the input's own font, and killing the browser autofill fill.
  MuiInputBase: {
    styleOverrides: {
      input: ({ theme }: { theme: Theme }) => {
        // Chrome / Edge / Safari paint a hard-coded fill on autofilled fields
        // — pale blue in light mode, slate-blue in dark — that ignores
        // `background-color`. The only reliable fix is a full-size inset
        // box-shadow in the field colour (paints over the UA fill), plus
        // `-webkit-text-fill-color` for the value (plain `color` is ignored
        // too). The long transition parks Chrome's re-fill animation far in
        // the future so a keystroke doesn't flash the blue back.
        //
        // The cover colour reads `FIELD_BACKGROUND` — the SAME lookup
        // `.MuiOutlinedInput-root`'s real fill uses (see MuiTextField
        // above) — rather than an independent guess here. `outline` /
        // `transparent` fields have no fill of their own, so their real
        // answer is `'transparent'` too; a literal `transparent` shadow
        // can't actually cancel the browser's own fill (only an opaque
        // cover can), but it can't silently drift out of sync with the
        // field's real look either, which a second, separately-maintained
        // colour choice here could.
        const autofillWith = (background: string) => ({
          WebkitBoxShadow: `0 0 0 100px ${background} inset`,
          WebkitTextFillColor: theme.palette.text.primary,
          caretColor: theme.palette.text.primary,
          fontFamily: theme.typography.bodyLarge.fontFamily,
          borderRadius: 'inherit',
          transition: 'background-color 100000s ease 0s'
        });
        const states = (background: string) => ({
          '&:-webkit-autofill': autofillWith(background),
          '&:-webkit-autofill:hover': autofillWith(background),
          '&:-webkit-autofill:focus': autofillWith(background),
          '&:-webkit-autofill:active': autofillWith(background),
          '&:-internal-autofill-selected': autofillWith(background)
        });
        return {
          fontFamily: theme.typography.bodyLarge.fontFamily,
          ...states(FIELD_BACKGROUND.solid(theme)),
          '[data-field-variant="outline"] &': states(
            FIELD_BACKGROUND.outline()
          ),
          '[data-field-variant="transparent"] &': states(
            FIELD_BACKGROUND.transparent()
          )
        };
      }
    }
  }
};
